import { useState } from "react";
import { StopCard } from "../components/StopCard";
import type { OpenExternalUrl } from "../lib/map-link";
import { departureBlockedReason, type DriverStop, type Slot } from "../lib/normalize";

export function StopGroups({
  stops,
  onSelect,
  onDepart,
  onArrive,
  onComplete,
  onDelay,
  openExternalUrl,
  acting = false,
  emptyMessage = "本日のルートはありません",
}: {
  stops: DriverStop[];
  onSelect: (s: DriverStop) => void;
  /** 一覧から直接の出発打刻。未指定なら打刻ボタンを出さない (「明日」タブ等) */
  onDepart?: (s: DriverStop) => void;
  /** 一覧から直接の到着打刻。未指定なら打刻ボタンを出さない */
  onArrive?: (s: DriverStop) => void;
  onComplete?: (s: DriverStop) => void;
  onDelay?: (s: DriverStop) => void;
  /** LIFF 内から Google マップを外部で開く関数。カードの「地図で開く」に渡す */
  openExternalUrl?: OpenExternalUrl;
  acting?: boolean;
  /** stop が 1 件も無いときの案内 (#768: 過去日を開いたら「本日の」では嘘になる) */
  emptyMessage?: string;
}) {
  // 進行中 → 最初の未着 の優先順で「次の目的地」を決める
  const nextStop =
    stops.find((s) => s.status === "active" && s.kind !== "depot") ??
    stops.find((s) => s.status === "pending" && s.kind !== "depot");

  const allGroups: { key: Slot; label: string; stops: DriverStop[] }[] = [
    {
      key: "early_morning",
      label: "早朝便 · 配達",
      stops: stops.filter((s) => s.slot === "early_morning"),
    },
    { key: "am", label: "午前便 · 配達", stops: stops.filter((s) => s.slot === "am") },
    { key: "break", label: "休憩", stops: stops.filter((s) => s.slot === "break") },
    { key: "pm", label: "午後便 · 食器回収・返却", stops: stops.filter((s) => s.slot === "pm") },
  ];
  const groups = allGroups.filter((g) => g.stops.length > 0);

  if (groups.length === 0) {
    return (
      <div className="p-6 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="px-2.5 pb-5 pt-3.5">
      {/* 次の目的地ピン留め */}
      {nextStop && (
        <div className="mb-3.5">
          <div className="flex items-center gap-1.5 px-2.5 pb-1.5 pt-1">
            <span className="h-2 w-2 rounded-full bg-[var(--ok)]" />
            <div className="text-[length:var(--fs-xs)] font-semibold tracking-[.06em] text-[var(--ok)]">
              次の目的地
            </div>
          </div>
          <StopCard
            stop={nextStop}
            onSelect={onSelect}
            isNext
            onDepart={onDepart}
            onArrive={onArrive}
            onComplete={onComplete}
            onDelay={onDelay}
            openExternalUrl={openExternalUrl}
            departDisabledReason={departureBlockedReason(stops, nextStop.id)}
            acting={acting}
          />
        </div>
      )}

      {groups.map((g) => (
        <GroupSection
          key={g.key}
          group={g}
          allStops={stops}
          onSelect={onSelect}
          onDepart={onDepart}
          onArrive={onArrive}
          onComplete={onComplete}
          onDelay={onDelay}
          openExternalUrl={openExternalUrl}
          acting={acting}
        />
      ))}
    </div>
  );
}

function GroupSection({
  group,
  allStops,
  onSelect,
  onDepart,
  onArrive,
  onComplete,
  onDelay,
  openExternalUrl,
  acting = false,
}: {
  group: { key: Slot; label: string; stops: DriverStop[] };
  allStops: DriverStop[];
  onSelect: (s: DriverStop) => void;
  onDepart?: (s: DriverStop) => void;
  onArrive?: (s: DriverStop) => void;
  onComplete?: (s: DriverStop) => void;
  onDelay?: (s: DriverStop) => void;
  /** LIFF 内から Google マップを外部で開く関数。カードの「地図で開く」に渡す */
  openExternalUrl?: OpenExternalUrl;
  acting?: boolean;
}) {
  const doneStops = group.stops.filter((s) => s.status === "done" || s.status === "cancelled");
  const activeStops = group.stops.filter((s) => s.status !== "done" && s.status !== "cancelled");
  const total = group.stops.length;
  const doneCount = doneStops.length;

  // 完了済みのみのグループは展開、混在グループは折りたたみ
  const [showDone, setShowDone] = useState(activeStops.length === 0);

  return (
    <div className="mb-3.5">
      <div className="flex items-center gap-1.5 px-2.5 pb-1.5 pt-1">
        <div className="text-[length:var(--fs-xs)] font-semibold uppercase tracking-[.08em] text-[var(--ink-3)]">
          {group.label}
        </div>
        <div className="h-px flex-1 bg-[var(--rule-soft)]" />
        {/* 進捗バッジ */}
        <div
          className={
            doneCount === total
              ? "num rounded-full bg-[var(--st-done-bg)] px-2 py-0.5 text-[length:var(--fs-xs)] font-semibold text-[var(--st-done-ink)]"
              : "num rounded-full bg-[var(--bg-raised)] px-2 py-0.5 text-[length:var(--fs-xs)] text-[var(--ink-3)]"
          }
        >
          {doneCount}/{total}
        </div>
      </div>

      {/* アクティブ/未着 stop（次の目的地も所属便から除外しない） */}
      {activeStops.map((s) => (
        <StopCard
          key={s.id}
          stop={s}
          onSelect={onSelect}
          onDepart={onDepart}
          onArrive={onArrive}
          onComplete={onComplete}
          onDelay={onDelay}
          openExternalUrl={openExternalUrl}
          departDisabledReason={departureBlockedReason(allStops, s.id)}
          acting={acting}
        />
      ))}

      {/* 完了済み stop（折りたたみ） */}
      {doneStops.length > 0 && (
        <>
          {showDone && doneStops.map((s) => <StopCard key={s.id} stop={s} onSelect={onSelect} />)}
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            className="mx-1.5 mb-1 flex w-[calc(100%-12px)] items-center justify-center gap-1 py-1.5 text-[length:var(--fs-sm)] text-[var(--ink-4)]"
          >
            {showDone
              ? `▲ 完了 ${doneStops.length}件を閉じる`
              : `▼ 完了 ${doneStops.length}件を表示`}
          </button>
        </>
      )}
    </div>
  );
}
